"use client";

import { BattleBoard, type ActiveCell } from "./BattleBoard";
import { NextPiece } from "./NextPiece";
import { TouchControls, type LegacyAction } from "./TouchControls";

export type BoardView = {
  active: ActiveCell[];
  /** Current falling-piece sequence; used to end one-shot touch drops after lock. */
  blockIndex?: number;
  board: number[][];
  nextCells: ReadonlyArray<readonly [number, number]>;
  nextPiece: number;
  pending: number;
  received: number;
  topOut: boolean;
  /** Cumulative in-match line clears, when the match transport provides it. */
  lines?: number;
  /** Number of pieces fixed during the current match, when available. */
  pieces?: number;
};

export type MatchStatus = {
  group: string;
  text: string;
};

type BattleArenaProps = {
  controlsEnabled: boolean;
  localSeat: 0 | 1 | 2;
  onInput: (action: LegacyAction, held: boolean) => void;
  p1: BoardView;
  p2: BoardView;
  p1Label?: string;
  p2Label?: string;
  p1Status?: MatchStatus;
  p2Status?: MatchStatus;
  quality: string;
};

export function BattleArena({
  controlsEnabled,
  localSeat,
  onInput,
  p1,
  p2,
  p1Label,
  p2Label,
  p1Status = { group: "E", text: "5級  0勝 0敗" },
  p2Status = { group: "E", text: "5級  0勝 0敗" },
  quality,
}: BattleArenaProps) {
  return (
    <div className="match-shell">
      <div aria-label="縦画面の次のブロック" className="portrait-next-row">
        <NextPiece cells={p1.nextCells} label="P1 NEXT" piece={p1.nextPiece} />
        <NextPiece cells={p2.nextCells} label="P2 NEXT" piece={p2.nextPiece} />
      </div>
      <div className={`arena arena-seat-${localSeat}`}>
        <div className="arena-board-slot arena-board-p1">
          <BattleBoard
            active={p1.active}
            activeBlockIndex={p1.blockIndex}
            board={p1.board}
            label={p1Label ?? (localSeat === 1 ? "PLAYER 1 / YOU" : "PLAYER 1")}
            local={localSeat === 1}
            controlsDisabled={!controlsEnabled}
            onInput={localSeat === 1 ? onInput : undefined}
            topOut={p1.topOut}
          />
        </div>

        <aside aria-label="対戦情報" className="versus-column">
          <div className="arena-next-row">
            <NextPiece cells={p1.nextCells} label="P1 NEXT" piece={p1.nextPiece} />
            <NextPiece cells={p2.nextCells} label="P2 NEXT" piece={p2.nextPiece} />
          </div>
          <div className="metrics">
            <div className="metric">
              <span className="metric-label">P1 IN</span>
              <strong>{p1.pending}</strong>
            </div>
            <div className="metric">
              <span className="metric-label">P2 IN</span>
              <strong>{p2.pending}</strong>
            </div>
            <div className="metric">
              <span className="metric-label">P1 TOTAL</span>
              <strong>{p1.received}</strong>
            </div>
            <div className="metric">
              <span className="metric-label">P2 TOTAL</span>
              <strong>{p2.received}</strong>
            </div>
          </div>
          <div aria-hidden="true" className="vs-mark">VS</div>
          <span className="connection-status">{quality}</span>
        </aside>

        <div className="arena-board-slot arena-board-p2">
          <BattleBoard
            active={p2.active}
            activeBlockIndex={p2.blockIndex}
            board={p2.board}
            label={p2Label ?? (localSeat === 2 ? "PLAYER 2 / YOU" : "PLAYER 2")}
            local={localSeat === 2}
            controlsDisabled={!controlsEnabled}
            onInput={localSeat === 2 ? onInput : undefined}
            topOut={p2.topOut}
          />
        </div>
      </div>

      <div aria-label="試合中のステータス" className="match-status-strip">
        <div className="match-status-player left">
          <strong>{p1Status.group}</strong>
          <span>{p1Status.text}</span>
        </div>
        <span aria-hidden="true" className="match-status-vs">VS</span>
        <div className="match-status-player right">
          <span>{p2Status.text}</span>
          <strong>{p2Status.group}</strong>
        </div>
      </div>

      {localSeat === 1 ? (
        <TouchControls disabled={!controlsEnabled} onInput={onInput} side="left" variant="practice" />
      ) : null}
      {localSeat === 2 ? (
        <TouchControls disabled={!controlsEnabled} onInput={onInput} side="right" variant="practice" />
      ) : null}
    </div>
  );
}
